
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isIOS } from "@/utils/device";
import { recordVoteTime } from "@/components/pwa/install-prompt";
import { lookupTeam } from "@/utils/vanity-url/team-lookup";
import { findLatestActivePoll } from "@/utils/vanity-url/poll-finder";

export interface VanityRedirectResult {
  isLoading: boolean;
  error: string | null;
  teamDisplayName: string | null;
  teamFound: boolean;
  shouldShowPWAPrompt: boolean;
}

export const useVanityRedirect = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamDisplayName, setTeamDisplayName] = useState<string | null>(null);
  const [shouldShowPWAPrompt, setShouldShowPWAPrompt] = useState(false);
  const [teamFound, setTeamFound] = useState(false);
  
  const handleError = (errorMessage: string) => {
    console.log("Vanity URL - Error handler called with:", errorMessage);
    recordVoteTime();
    if (isIOS()) {
      setShouldShowPWAPrompt(true);
    }
    setError(errorMessage);
    setIsLoading(false);
  };
  
  useEffect(() => {
    const findTeamAndLatestPoll = async () => {
      try {
        setIsLoading(true);
        console.log("Vanity URL - Starting URL redirect with params:", {
          params,
          pathname: window.location.pathname,
          url: window.location.href
        });
        
        // Look up the team based on URL parameters
        const { team, error: teamError } = await lookupTeam({
          urlSlug: params.urlSlug,
          teamName: params.teamName,
          clubAcronym: params.clubAcronym
        });
        
        if (teamError || !team) {
          handleError(teamError || "Team not found");
          return;
        }
        
        setTeamFound(true);
        setTeamDisplayName(team.name);
        
        // Find latest active poll
        const { gameId, error: pollError } = await findLatestActivePoll(team.id, team.name);
        
        if (pollError || !gameId) {
          handleError(pollError || `No active polls found for ${team.name}`);
          return;
        }
        
        console.log("Vanity URL - Redirecting to game:", gameId);
        navigate(`/game/${gameId}`);
        
      } catch (error) {
        console.error("Error in vanity URL redirect:", error);
        handleError("Something went wrong. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    
    findTeamAndLatestPoll();
  }, [params, navigate]);
  
  return {
    isLoading,
    error,
    teamDisplayName,
    teamFound,
    shouldShowPWAPrompt
  };
};
